import { useState } from 'react';
import { BUTTON_VARIANTS } from 'ui/button';
import { FormField } from 'ui/formField';
import { Delete } from 'ui/icons';
import {
  ButtonController,
  ButtonTypeVariationContainer,
  ButtonTypeVariationHeaderContainer,
  DeleteIconWrapper,
  InputWrapper,
} from 'modules/templateEditor/common/styles';
import { ExpandableChevron, LabelWithLink, getIsEditMode } from 'modules/templateEditor/common';
import { InAppContentDefinition, RokuButtonContent } from 'modules/graphql/types';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { SETTINGS_DISPLAY_CONTENT } from 'modules/templateEditor/EditInApp/constants';
import { AccordionContent } from 'ui/accordion';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { ColorPicker } from '../shared/ColorPicker';
import {
  TitleWrapper,
  PlaceholderInputWrapper,
  StyledInputText,
  StyledLabel,
  Divider,
  StyledAccordion,
  StyledAccordionLabel,
} from './styles';

type Props = {
  button: RokuButtonContent;
  index: number;
};

export const ButtonTypeVariations = ({ button, index }: Props) => {
  const { view } = useQueryParamContext();
  const { liveTemplate, setCurrentMediumField, isEditMode } = useEditableFieldsContext();
  const isEditModeVisible = getIsEditMode(view, isEditMode);
  const { ottPayload } = (liveTemplate?.mediumData || {}) as InAppContentDefinition;
  const rokuButtons = (ottPayload?.buttons || []) as RokuButtonContent[];
  const [isExpanded, setIsExpanded] = useState<boolean>(true);

  const onChangeButton = (field: keyof RokuButtonContent, newValue: string) => {
    const updatedButton = {
      ...button,
      [field]: newValue,
    };
    rokuButtons[index] = updatedButton;
    setCurrentMediumField('ottPayload', { ...ottPayload, buttons: rokuButtons });
  };

  return (
    <ButtonTypeVariationContainer data-test="action-button-tile">
      <ButtonTypeVariationHeaderContainer isExpanded={isExpanded}>
        <TitleWrapper>{`Button ${index + 1}`}</TitleWrapper>
        <ButtonController data-test="action-button-header-ctrl">
          <DeleteIconWrapper
            onClick={() => {
              rokuButtons.splice(index, 1);
              setCurrentMediumField('ottPayload', { ...ottPayload, buttons: rokuButtons });
            }}
            variant={BUTTON_VARIANTS.GHOST}
          >
            <Delete width={24} height={24} />
          </DeleteIconWrapper>
          <ExpandableChevron
            isExpanded={isExpanded}
            onClick={(result: boolean) => setIsExpanded(result)}
          />
        </ButtonController>
      </ButtonTypeVariationHeaderContainer>

      {isExpanded && (
        <InputWrapper>
          {/** buttonText */}
          <FormField labelText="Button text">
            <PlaceholderInputWrapper>
              <StyledInputText
                id="buttonText"
                onChange={(buttonText) => onChangeButton('buttonText', buttonText)}
                value={button.buttonText || ''}
                maxLength={20}
              />
            </PlaceholderInputWrapper>
          </FormField>

          <StyledAccordion id="action-accordion">
            <StyledAccordionLabel>
              <StyledLabel>ACTION</StyledLabel>
            </StyledAccordionLabel>
            <AccordionContent>
              {/** buttonDeeplink */}
              <FormField
                labelText="Deep link"
                helpText={
                  isEditModeVisible && (
                    <LabelWithLink
                      text={SETTINGS_DISPLAY_CONTENT.roku.actionButtons.deepLinkHelpText}
                      url={SETTINGS_DISPLAY_CONTENT.roku.actionButtons.deepLinkDocs}
                      openInNewTab
                    />
                  )
                }
              >
                <PlaceholderInputWrapper>
                  <StyledInputText
                    id="buttonDeeplink"
                    onChange={(deepLink) => onChangeButton('buttonDeeplink', deepLink)}
                    value={button.buttonDeeplink || ''}
                  />
                </PlaceholderInputWrapper>
              </FormField>
            </AccordionContent>
          </StyledAccordion>

          <Divider />

          <StyledAccordion id="default-style-accordion">
            <StyledAccordionLabel>
              <StyledLabel>DEFAULT STYLE</StyledLabel>
            </StyledAccordionLabel>
            <AccordionContent>
              {/** buttonTextColorUnFocused */}
              <FormField labelText="Text color">
                <PlaceholderInputWrapper>
                  <ColorPicker
                    hexColor={button.buttonTextColorUnFocused}
                    onChange={(hexColor) => onChangeButton('buttonTextColorUnFocused', hexColor)}
                    dataTestIdentifier={index}
                  />
                </PlaceholderInputWrapper>
              </FormField>

              {/** buttonColorUnFocused */}
              <FormField labelText="Button color">
                <PlaceholderInputWrapper>
                  <ColorPicker
                    hexColor={button.buttonColorUnFocused}
                    onChange={(hexColor) => onChangeButton('buttonColorUnFocused', hexColor)}
                    dataTestIdentifier={index}
                  />
                </PlaceholderInputWrapper>
              </FormField>

              {/** buttonBorderColor */}
              <FormField labelText="Border color">
                <PlaceholderInputWrapper>
                  <ColorPicker
                    hexColor={button.buttonBorderColor}
                    onChange={(hexColor) => onChangeButton('buttonBorderColor', hexColor)}
                    dataTestIdentifier={index}
                  />
                </PlaceholderInputWrapper>
              </FormField>
            </AccordionContent>
          </StyledAccordion>

          <Divider />

          <StyledAccordion id="hover-style-accordion">
            <StyledAccordionLabel>
              <StyledLabel>HOVER STYLE</StyledLabel>
            </StyledAccordionLabel>
            <AccordionContent>
              {/** buttonTextColorFocused */}
              <FormField labelText="Hover text color">
                <PlaceholderInputWrapper>
                  <ColorPicker
                    hexColor={button.buttonTextColorFocused}
                    onChange={(hexColor) => onChangeButton('buttonTextColorFocused', hexColor)}
                    dataTestIdentifier={index}
                  />
                </PlaceholderInputWrapper>
              </FormField>

              {/** buttonColorFocused */}
              <FormField labelText="Hover button color">
                <PlaceholderInputWrapper>
                  <ColorPicker
                    hexColor={button.buttonColorFocused}
                    onChange={(hexColor) => onChangeButton('buttonColorFocused', hexColor)}
                    dataTestIdentifier={index}
                  />
                </PlaceholderInputWrapper>
              </FormField>
            </AccordionContent>
          </StyledAccordion>
        </InputWrapper>
      )}
    </ButtonTypeVariationContainer>
  );
};
